// apex_chart_controller の CSV 出力まわりの純粋関数。
// コントローラーは apexcharts / Stimulus を import するため node --test から読めないので、ここに切り出している。

const pad2 = (value) => value.toString().padStart(2, '0')

const toValidDate = (value) => {
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

// ブラウザのローカルタイム(=日本時間)で YYYY-MM-DD にする。変換できなければ null
export const formatDateToYMD = (value) => {
  const date = toValidDate(value)
  if (!date) {
    return null
  }

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

// ブラウザのローカルタイム(=日本時間)で YYYY-MM-DD HH:mm にする。変換できなければ null
export const formatDateTimeToYMDHM = (value) => {
  const date = toValidDate(value)
  if (!date) {
    return null
  }

  return `${formatDateToYMD(date)} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`
}

// config.export を正規の指定先とし、既存の options 配下も後方互換として受け付ける。
export const resolveCsvExportOptions = (config) => {
  return config.export?.csv ||
         config.options?.export?.csv ||
         config.options?.chart_options?.export?.csv ||
         {}
}

// ApexCharts v5 は非表示（hidden/凡例OFF）系列の data を config から退避するため、
// 標準の exportToCSV では該当系列の列が全て空になる（v4 からの回帰）。
// サーバーから受け取った元データ（configValue）から CSV を自前生成して置き換える。
//
// datetime 軸の category 列は既定で日付(YYYY-MM-DD)。時間単位のデータなど、同じ日の行を区別したいチャートは
// export.csv.includeTime: true を指定すると YYYY-MM-DD HH:mm で出す
// (ChartHelper 経由なら chart_options: { export: { csv: { includeTime: true } } })。
export const buildCsvFromConfig = (config, columnDelimiter = ',') => {
  const seriesList = config.series
  if (!Array.isArray(seriesList) || !Array.isArray(seriesList[0]?.data)) {
    return null
  }

  const xaxisType = config.xaxis?.type || config.options?.xaxis_type
  const categories = config.xaxis?.categories?.length ? config.xaxis.categories : null
  const sanitize = (value) => String(value).split(columnDelimiter).join('')
  const formatDatetime = resolveCsvExportOptions(config).includeTime === true ? formatDateTimeToYMDHM : formatDateToYMD

  // x値をキーに全系列の値を揃える（系列間でx値がずれていても行を合成できるようにする）
  const rowsMap = new Map()
  seriesList.forEach((s, si) => {
    (s.data || []).forEach((point, pi) => {
      const isXY = point !== null && typeof point === 'object'
      const x = isXY ? point.x : (categories ? categories[pi] : pi)
      const y = isXY ? point.y : point
      const key = String(x)
      if (!rowsMap.has(key)) {
        rowsMap.set(key, { x, values: new Array(seriesList.length).fill('') })
      }
      rowsMap.get(key).values[si] = (y === null || y === undefined) ? '' : y
    })
  })

  const rows = Array.from(rowsMap.values())
  if (rows.every((row) => typeof row.x === 'number' && Number.isFinite(row.x))) {
    rows.sort((a, b) => a.x - b.x)
  }

  const formatCategory = (x) => {
    if (xaxisType === 'datetime') {
      const formatted = formatDatetime(x)
      if (formatted) return formatted
    }
    return sanitize(x)
  }

  const header = [
    'category',
    ...seriesList.map((s, i) => sanitize(s.name ?? `series-${i}`))
  ].join(columnDelimiter)
  const lines = rows.map((row) => [formatCategory(row.x), ...row.values].join(columnDelimiter))

  return '\uFEFF' + [header, ...lines].join('\n')
}
